import type { CSSProperties } from "react";
import type { AppDefinition } from "./OsAppRegistry";

/** Darker glyph colors in light mode without changing tile backgrounds. */
function adaptDualToneIconColor(iconColor?: string, fallback = "text-slate-500"): string {
  const base = iconColor || fallback;
  if (base.includes("dark:")) return base;

  const match = base.match(/^text-([a-z]+)-(\d+)$/);
  if (!match) return base;

  const [, name, shade] = match;
  const n = parseInt(shade, 10);
  const lightShade = n <= 400 ? 700 : n <= 500 ? 700 : 800;
  return `text-${name}-${lightShade} dark:${base}`;
}

/** Opaque tile shell shared by Apps button + colored dock icons (no transparency blob). */
export const DOCK_TILE_SHELL_CLASSES =
  "bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 shadow-sm dark:shadow-lg";

export function getDockIconBackgroundClasses(
  theme?: AppDefinition["colorTheme"],
  isHoverOnly?: boolean,
  forceIconStyle?: "gradient" | "colored",
): { classes: string; style?: CSSProperties } {
  if (!theme) return { classes: "bg-slate-800/90 text-slate-300" };

  const shadowClasses = theme.shadow || "shadow-lg";

  if (isHoverOnly) {
    return {
      classes: `${DOCK_TILE_SHELL_CLASSES} text-slate-600 dark:text-slate-400 transition-all duration-200`,
    };
  }

  if (forceIconStyle === "colored") {
    const iconColor = adaptDualToneIconColor(
      theme.coloredIconColor,
      theme.iconColor,
    );

    return {
      classes: `${DOCK_TILE_SHELL_CLASSES} ${iconColor}`,
    };
  }

  switch (theme.type) {
    case "gradient":
      return {
        classes: `bg-gradient-to-br ${theme.colors} ${theme.iconColor} ${shadowClasses}`,
      };
    case "radial":
      return {
        classes: `bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] ${theme.colors} ${theme.iconColor} ${shadowClasses}`,
      };
    case "conic":
      return {
        classes: `bg-[conic-gradient(from_135deg_at_50%_50%,_var(--tw-gradient-stops))] ${theme.colors} ${theme.iconColor} ${shadowClasses}`,
      };
    case "mesh":
      return {
        classes: `bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] ${theme.colors} ${theme.iconColor} ${shadowClasses}`,
      };
    case "solid":
      return { classes: `${theme.colors} ${theme.iconColor} ${shadowClasses}` };
    case "glass":
      return { classes: `${theme.colors} ${theme.iconColor} shadow-md` };
    case "bordered":
      return { classes: `${theme.colors} ${theme.iconColor} shadow-sm` };
    default:
      return {
        classes: `bg-slate-800/90 ${theme.iconColor || "text-slate-300"} ${shadowClasses}`,
      };
  }
}

/** Light tile shell for the Apps grid button (no per-app color blob). */
export const DOCK_APPS_BUTTON_TILE_CLASSES = DOCK_TILE_SHELL_CLASSES;

/** Shared corner radius for dock chrome + icon tiles. */
export const DOCK_CHROME_RADIUS_CLASS = "rounded-2xl";

export const DOCK_ICON_TILE_CLASS =
  `flex h-12 w-12 items-center justify-center ${DOCK_CHROME_RADIUS_CLASS}`;

/** Fixed-height row so open indicators align every dock slot. */
export const DOCK_ICON_INDICATOR_ROW_CLASS =
  "mt-1 flex h-[3px] w-3 items-center justify-center";

export const DOCK_BUTTON_BASE_CLASS =
  `group relative flex cursor-pointer items-center justify-center ${DOCK_CHROME_RADIUS_CLASS} outline-none transition-all duration-200 focus:outline-none focus-visible:outline-none`;

/** Dock slot hover / active — subtle slate wash in light mode, white overlay in dark. */
export function getDockButtonStateClasses(isActive = false): string {
  return isActive
    ? "bg-slate-200/80 dark:bg-white/10"
    : "hover:bg-slate-200/50 dark:hover:bg-white/10";
}

/** Glass + frame classes shared by the dock bar and apps popover. */
export const DOCK_CHROME_GLASS_CLASSES =
  "bg-gradient-to-bl from-white/80 to-white/60 backdrop-blur-xl dark:from-slate-900/50 dark:to-slate-900/20";

export const DOCK_CHROME_FRAME_CLASSES =
  "border border-slate-200/80 shadow-lg shadow-black/10 dark:border-white/10 dark:shadow-black/20";

export const DOCK_APP_ICON_CLASS =
  "text-xl [--fa-primary-opacity:1] [--fa-secondary-opacity:0.72] dark:[--fa-secondary-opacity:0.4]";

/** Active / open indicator under dock icons. */
export const DOCK_OPEN_INDICATOR_ACTIVE_CLASS = "bg-slate-700 dark:bg-white";
export const DOCK_OPEN_INDICATOR_IDLE_CLASS = "bg-slate-500 dark:bg-white/50";
